import type { ClientsSortKey } from '../types/clients';
import { ProspectSalesModel } from '../types/prospects';

// CONFIGURATION
export const TAG_MANAGER_ID: string = import.meta.env.VITE_GTM_SITE_KEY ?? '';
export const TAG_MANAGER_ENV: string = import.meta.env.VITE_GTM_ENV ?? '';
export const TAG_MANAGER_AUTH: string = import.meta.env.VITE_GTM_AUTH ?? '';

export const EVENT_SEPARATOR = ' - ';
export const PROJECT_EVENT_TAG = 'Brokers Portal';
export const USER_ATTRIBUTES_IDENTIFIER = 'bp-';

// ACTIONS
export const CLICK_ACTION = 'Click';
export const INPUT_ACTION = 'Input';
export const ERROR_ACTION = 'Error';
export const LOG_ACTION = 'Log';
export const VIEW_ACTION = 'View';
export const RESET_VARIABLES = 'ResetVariables';
export const SET_ACTION = 'Set';

// AUTH EVENTS
export const LOGIN = 'Login';
export const LOGOUT = 'Logout';
export const ENTER_EMAIL = 'EnterEmail';
export const CONTINUE_CODE_VERIFICATION = 'ContinueCodeVerification';
export const RESEND_CODE_VERIFICATION = 'ResendCodeVerification';
export const CREATE_ACCOUNT = 'CreateAccount';
export const CREATE_PASSWORD = 'CreatePassword';
export const PASSWORD_CONTINUE = 'PasswordContinue';
export const FORGOT_PASSWORD_PAGE = 'ForgotPassword';
export const RESTORE_PASSWORD = 'RestorePassword';

// GLOBAL EVENTS
export const IDENTIFY_EVENT = 'Identify';

// PROSPECT EVENTS
export const NEW_PROSPECT = 'NewProspect';
export const CREATE_PROSPECT = 'CreateProspect';
export const VIEW_FULL_PROFILE = 'ViewFullProfile';
export const SAVE_COMPANY_DATA = 'SaveCompanyData';
export const SAVE_PRIMARY_CONTACT = 'SavePrimaryContact';
export const SAVE_BILLING_CONTACT = 'SaveBillingContact';

export const OPTION_SAME_CONTACT = { option: 'same-contact' };
export const OPTION_NEW_CONTACT = { option: 'new-contact' };
export const SALES_MODEL_PARAM: Record<ProspectSalesModel, string> = {
  [ProspectSalesModel.AppAndInsurance]: 'betterfly_benefits',
  [ProspectSalesModel.AppOnly]: 'post_enrollment'
};

// CLIENT EVENTS
export const VIEW_CLIENT_PROFILE = 'ViewClientProfile';
export const NEW_CLIENT_QUOTE = 'NewClientQuote';
export const VIEW_PLAN_DETAILS = 'ViewPlanDetails';
export const SELECT_CLIENT_BUCKET = 'SelectClientBucket';
export const SEARCH_CLIENTS = 'SearchClients';
export const SORT_CLIENTS = 'SortClients';

// CLIENT EVENT PARAMS
export const CLIENT_SORT_COLUMN: Partial<Record<ClientsSortKey, string>> = {
  companyName: 'company',
  workforceSize: 'size',
  enrollmentWindow: 'date'
};

export const SEARCH_CLIENTS_DEBOUNCE_MS = 800;

//SIDEBAR EVENTS
export const SIDEBAR_MENU = 'SideBarMenu';

//QUOTE EVENTS
export const GET_QUOTE = 'GetQuote';
export const NEW_QUOTE = 'NewQuote';
export const EDIT_QUOTE = 'EditQuote';
export const QUOTATION_BACK = 'QuotationBack';
export const QUOTE_NAME = 'QuoteName';
export const QUOTE_SEGMENT = 'QuoteSegment';
export const QUOTE_HEADCOUNT = 'QuoteHeadcount';
export const SELECT_PRODUCT = 'SelectProduct';
export const SELECT_PRODUCT_OPTION = 'SelectProductOption';
export const SELECT_TOBACCO_STATUS = 'SelectTobaccoStatus';
export const AVG_EMPLOYEE_AGE = 'AvgEmployeeAge';
export const VIEW_RATE_TABLE = 'ViewRateTable';
export const UPLOAD_CENSUS = 'UploadCensus';
export const REMOVE_CENSUS = 'RemoveCensus';
export const SAVE_QUOTE = 'SaveQuote';
export const UPDATE_QUOTE = 'UpdateQuote';
export const KEEP_EDITING = 'KeepEditing';
export const LEAVE_WITHOUT_SAVING = 'LeaveWithoutSaving';
export const ACCEPT_QUOTE = 'AcceptQuote';
export const UNACCEPT_QUOTE = 'UnacceptQuote';
export const DOWNLOAD_PDF_QUOTE = 'DownloadPDFQuote';
export const MARK_AS_INACTIVE = 'MarkAsInactive';
export const MARK_AS_LOST = 'MarkAsLost';

// ACTIVATION EVENTS
export const MOVE_TO_ACTIVATION = 'MoveToActivation';
export const COMPLETE_ACTIVATION_NOW = 'CompleteActivationNow';
export const SAVE_AS_PENDING_ACTIVATION = 'SaveAsPendingActivation';
export const COMPLETE_ACTIVATION = 'CompleteActivation';
export const ACTIVATION_CHECKLIST_ITEM = 'ActivationChecklistItem';
export const ACTIVATION_UPLOAD_CENSUS = 'ActivationUploadCensus';
export const ACTIVATION_DOWNLOAD_TEMPLATE = 'ActivationDownloadTemplate';
export const ACTIVATION_REMOVE_CENSUS = 'ActivationRemoveCensus';
export const ACTIVATION_CENSUS_HELP = 'ActivationCensusHelp';
export const ACTIVATION_VALIDATE_CENSUS = 'ActivationValidateCensus';
export const ACTIVATION_CENSUS_BACKGROUND = 'ActivationCensusBackground';
export const CENSUS_VALIDATION_RESULT = 'CensusValidationResult';
export const ACTIVATION_APPLY_CENSUS = 'ActivationApplyCensus';
export const ACTIVATION_ACCEPT_NEW_PRICE = 'ActivationAcceptNewPrice';
export const ACTIVATION_REJECT_NEW_PRICE = 'ActivationRejectNewPrice';
export const ACTIVATION_RETRY_CENSUS = 'ActivationRetryCensus';
export const ACTIVATION_CENSUS_CLOSE = 'ActivationCensusClose';
export const SAVE_PAY_FREQUENCY = 'SavePayFrequency';
export const CONFIRM_ACTIVATION = 'ConfirmActivation';
export const SUBMIT_ACTIVATION = 'SubmitActivation';
export const CANCEL_ACTIVATION = 'CancelActivation';

export const SUBMIT_BENEFITS_DOCUMENTS = 'SubmitBenefitsDocuments';

export const BENEFITS_DOCUMENTS_SOURCE = {
  activationSetup: 'activation-setup',
  planSetup: 'plan-setup',
  activePlan: 'active-plan'
} as const;

export type BenefitsDocumentsSource = (typeof BENEFITS_DOCUMENTS_SOURCE)[keyof typeof BENEFITS_DOCUMENTS_SOURCE];

// EVENT PROVIDERS
export const BROKERS_PORTAL_PROVIDER = 'BrokersPortal';
export const GOOGLE_PROVIDER = 'Google';
export const MICROSOFT_PROVIDER = 'Microsoft';

// EVENT SECTIONS
export const SECTION_CREATE_ACCOUNT = { section: 'create-account' };
export const SECTION_FORGOT_PASSWORD = { section: 'forgot-password' };

// EVENT STATES
export const STATE_ERROR = { state: 'error' };
export const STATE_SUCCESSFUL = { state: 'successful' };

// EVENT SCREENS

export const SCREEN_PROSPECT_TABLE = { screen: 'prospect-table' };
export const SCREEN_FULL_PROFILE = { screen: 'full profile' };

// EVENT METHODS
export const MANUAL_METHOD = { method: 'MANUAL-CREDENTIALS' };

export const SOURCE_PANEL = { source: 'panel' };
export const SOURCE_MODAL = { source: 'modal' };
export const SOURCE_PROFILE = { source: 'profile' };

// EVENT SCOPES
export const SCOPE_PANEL = { scope: 'panel' };
export const SCOPE_PROFILE = { scope: 'profile' };

export const CENSUS_RESULT = {
  success: 'success',
  dataErrors: 'data-errors',
  belowMinimum: 'below-minimum',
  priceMismatch: 'price-mismatch',
  serviceError: 'service-error'
} as const;

// ACTIVATION CHECKLIST ITEM EVENT PARAMS
export const CHECKLIST_TARGET = {
  company: 'company',
  contact: 'contact',
  billing: 'billing',
  employeeData: 'employee-data',
  payrollSchedule: 'payroll-schedule',
  quoteAcceptance: 'quote-acceptance'
} as const;
